/**
 * 只读 @ 资产提及文本组件（v1.4.87 新建；自 FinalPromptModal 的 MentionTextarea 高亮层与
 * hover 资产卡精简为纯展示版，供批量生成确认弹窗的锚定行/提示词全文复用）：
 * - 三交替正则分段渲染（词典最大匹配截断，同 FinalPromptModal v1.4.68/76）：
 *   「@+词典名 | @+占位段 | 词典裸名」，词典 = collectAssetMatchNames（资产名 + 别名）；
 *   @+词典名与词典裸名渲染 text-blue-300（与节点底部参数面板 @ 提及同一蓝色语义）、
 *   @+占位段（@角色A 等词典外提及）渲染 text-red-400；只读场景文本原样展示、不补 @ 前缀。
 * - enableBareNames=false 时跳过「词典裸名」兜底（锚定行自身同时含裸名与 @名各一次，
 *   全高亮会同词双高亮，故锚定行传 false 只认 @+名）。
 * - 词典段 hover 弹出资产图预览卡（对齐最终合成提示词面板 v1.4.71/72 的 264px 卡：
 *   鼠标旁边弹出，右/下空间不足翻转左/上、clamp 视口 12px；图区高度按图片真实宽高比
 *   自适应 clamp 132~360，onLoad 建档缓存；卡内 @名称 + 类别标签 + 描述 line-clamp-3，
 *   无图回落首字大字+「暂无设定图」；v1.4.100 曾放大 1 倍至 528，已随主版回退 264；
 *   只读场景无 textarea 遮挡，直接在词典段 span 上挂 onMouseEnter/Leave 即可）。
 * - mutedNames：命中段（含别名归一）置灰 text-white/35（仍可 hover 查看卡片）——锚定行用
 *   它标注「资产图未生成、不会注入参考图」的资产。
 */
import { MouseEvent as ReactMouseEvent, ReactNode, useCallback, useMemo, useState } from 'react';
import {
  SCRIPT_ASSET_KIND_LABELS,
  ScriptAsset,
  buildAliasNameMap,
  collectAssetMatchNames,
} from '../../types/scriptNode';

/** 构建词典正则片段（去重 trim、长度降序防前缀截断、正则元字符转义；同 FinalPromptModal） */
function buildDictAlt(names: string[]): string {
  return [...new Set(names.map((n) => n.trim()).filter(Boolean))]
    .sort((a, b) => b.length - a.length)
    .map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
}

/** 图片自然宽高比缓存（imageUrl → w/h）：首次 onLoad 建档，此后 hover 即时按真实比例预留高度 */
const assetImgAspectCache = new Map<string, number>();

/**
 * 图区高度自适应；v1.4.72 同款：卡宽 264 固定，高 = 264 / 宽高比，clamp 132~360；
 * 未缓存/无图回落方图 264。16:9 ≈149、4:3 ≈198、1:1 = 264。
 * （v1.4.100 曾放大 1 倍至 528，已随 FinalPromptModal 回退原 264 尺寸）
 */
function previewImageH(aspect?: number): number {
  if (!aspect || !Number.isFinite(aspect) || aspect <= 0) return 264;
  return Math.min(Math.max(Math.round(264 / aspect), 132), 360);
}

/** 三交替分段渲染（同 FinalPromptModal renderMentionParts；muted 段置灰，hover 段加背景反馈） */
function renderMentionParts(
  text: string,
  dict: string,
  bareNames: boolean,
  muted: ReadonlySet<string>,
  hoveredName: string | undefined,
  onEnter: (e: ReactMouseEvent<HTMLSpanElement>, name: string) => void,
  onLeave: () => void,
): ReactNode[] {
  if (!dict) return [text];
  const re = new RegExp(
    `@(${dict})|(@[A-Za-z0-9_\\-\\u4e00-\\u9fff]+)${bareNames ? `|(${dict})` : ''}`,
    'g',
  );
  const parts: ReactNode[] = [];
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const dictName = m[1] ?? m[3];
    const isPlaceholder = !dictName;
    parts.push(
      <span
        key={key++}
        data-asset-name={dictName}
        onMouseEnter={dictName ? (e) => onEnter(e, dictName) : undefined}
        onMouseLeave={dictName ? onLeave : undefined}
        className={`${
          isPlaceholder ? 'text-red-400' : dictName && muted.has(dictName) ? 'text-white/35' : 'text-blue-300'
        }${dictName && hoveredName === dictName ? ' bg-sky-500/25 rounded-sm' : ''}`}
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function AssetMentionText({
  text,
  assets,
  mutedNames,
  enableBareNames = true,
  className,
}: {
  text: string;
  /** 资产列表（@ 提及高亮词典 + hover 预览卡数据源） */
  assets: ScriptAsset[];
  /** 置灰资产主名（未出图资产；其别名段同置灰），@名 灰显但仍可 hover 查看卡片 */
  mutedNames?: string[];
  /** 是否高亮「词典裸名」（默认开；锚定行传关，避免同词「裸名+@名」双高亮） */
  enableBareNames?: boolean;
  className?: string;
}) {
  const [hover, setHover] = useState<{
    name: string;
    asset: ScriptAsset | undefined;
    x: number;
    y: number;
  } | null>(null);
  const aliasMap = useMemo(() => buildAliasNameMap(assets), [assets]);
  const dict = useMemo(() => buildDictAlt(collectAssetMatchNames(assets)), [assets]);
  // muted 展开：主名 + 该资产全部别名（命中段 data-asset-name 可能是别称）
  const muted = useMemo(() => {
    const set = new Set<string>();
    for (const name of mutedNames ?? []) {
      set.add(name);
      for (const a of assets) {
        if (a.name === name) for (const alias of a.aliases ?? []) set.add(alias);
      }
    }
    return set;
  }, [mutedNames, assets]);

  // 位置算法同 FinalPromptModal v1.4.71：鼠标旁边弹 264px 大卡，右/下空间不足翻转左/上，clamp 视口 12px
  const handleEnter = useCallback(
    (e: ReactMouseEvent<HTMLSpanElement>, name: string) => {
      const asset = assets.find((a) => a.name === (aliasMap[name] ?? name));
      const CARD_W = 264;
      // 信息区高度（名称 13px/类别徽标 10px/描述 11px + 内边距）约 100
      const INFO_H = 100;
      const GAP = 16;
      const MARGIN = 12;
      const aspect = asset?.imageUrl ? assetImgAspectCache.get(asset.imageUrl) : undefined;
      const CARD_H = previewImageH(aspect) + INFO_H;
      const cx = e.clientX;
      const cy = e.clientY;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let x = cx + GAP;
      if (x + CARD_W > vw - MARGIN) x = cx - GAP - CARD_W;
      x = Math.min(Math.max(x, MARGIN), Math.max(vw - CARD_W - MARGIN, MARGIN));
      let y = cy + GAP;
      if (y + CARD_H > vh - MARGIN) y = cy - GAP - CARD_H;
      y = Math.min(Math.max(y, MARGIN), Math.max(vh - CARD_H - MARGIN, MARGIN));
      setHover({ name, asset, x, y });
    },
    [assets, aliasMap],
  );
  const hideHover = useCallback(() => setHover((prev) => (prev ? null : prev)), []);

  const parts = useMemo(
    () => renderMentionParts(text, dict, enableBareNames, muted, hover?.name, handleEnter, hideHover),
    [text, dict, enableBareNames, muted, hover?.name, handleEnter, hideHover],
  );
  // 图区高度按真实宽高比自适应（缓存命中即时正确；未命中默认方图，onLoad 后校正）
  const hoverImageH = previewImageH(
    hover?.asset?.imageUrl ? assetImgAspectCache.get(hover.asset.imageUrl) : undefined,
  );

  return (
    <>
      <span className={className}>{parts}</span>
      {hover && (
        <div
          className="fixed z-[400] pointer-events-none w-[264px] rounded-2xl bg-[#141416] border border-white/10 shadow-[0_12px_48px_rgba(0,0,0,0.6)] overflow-hidden"
          style={{ left: hover.x, top: hover.y }}
        >
          <div
            className="flex items-center justify-center bg-[#0d0d0f] border-b border-white/[0.06]"
            style={{ height: hoverImageH }}
          >
            {hover.asset?.imageUrl ? (
              <img
                src={hover.asset.imageUrl}
                alt={hover.name}
                className="h-full w-full object-contain"
                draggable={false}
                onLoad={(e) => {
                  const el = e.currentTarget;
                  const ratio =
                    el.naturalWidth && el.naturalHeight ? el.naturalWidth / el.naturalHeight : 0;
                  setHover((prev) => {
                    const url = prev?.asset?.imageUrl;
                    if (!url || !ratio || assetImgAspectCache.get(url) === ratio) return prev;
                    assetImgAspectCache.set(url, ratio);
                    // 缓存首次建档：仅重算图区高度，卡片位置保持冻结不跳
                    return { ...prev };
                  });
                }}
              />
            ) : (
              <span className="flex flex-col items-center gap-2">
                <span className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/[0.06] border border-white/10 text-[24px] font-medium text-white/60">
                  {hover.name.slice(0, 1)}
                </span>
                <span className="text-[11px] text-white/30">暂无设定图</span>
              </span>
            )}
          </div>
          <div className="flex flex-col gap-1 px-3.5 py-3">
            <div className="flex items-center justify-between gap-1">
              <span className="min-w-0 truncate text-[13px] text-white/90">@{hover.name}</span>
              <span className="shrink-0 rounded bg-white/[0.06] px-1.5 py-0.5 text-[10px] text-white/50">
                {hover.asset ? SCRIPT_ASSET_KIND_LABELS[hover.asset.kind] : '资产'}
              </span>
            </div>
            {hover.asset?.description.trim() && (
              <p className="text-[11px] leading-relaxed text-white/40 line-clamp-3">
                {hover.asset.description}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
